<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useCaltopoStore } from '@/stores/caltopo'
import { useOpsStore } from '@/stores/ops'
import type { CalTopoMap } from '@/types'
import FosButton from '@/components/ui/FosButton.vue'
import FosSelect from '@/components/ui/FosSelect.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosDialog from '@/components/ui/FosDialog.vue'

const props = defineProps<{
  incidentId: string
  opId: string
  opNumber: number
  connected: boolean
  mapName?: string | null
  fullWidth?: boolean
}>()

const caltopoStore = useCaltopoStore()
const opsStore = useOpsStore()

const RING_R = 10
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R

const now = ref(Date.now())
let _tick: ReturnType<typeof setInterval> | null = null
onMounted(() => { _tick = setInterval(() => { now.value = Date.now() }, 1000) })
onUnmounted(() => { if (_tick) clearInterval(_tick) })

const pollIntervalMs = computed(() => opsStore.selectedOp?.caltopo_poll_interval_ms ?? 120_000)

const ringProgress = computed(() => {
  if (!caltopoStore.lastSyncedAt) return 0
  const elapsed = now.value - caltopoStore.lastSyncedAt.getTime()
  return Math.min(elapsed / pollIntervalMs.value, 1)
})

const ringDashOffset = computed(() => RING_CIRCUMFERENCE * ringProgress.value)

const secondsUntilSync = computed(() => {
  if (!caltopoStore.lastSyncedAt) return null
  const elapsed = now.value - caltopoStore.lastSyncedAt.getTime()
  return Math.ceil(Math.max(pollIntervalMs.value - elapsed, 0) / 1000)
})

function formatCountdown(seconds: number): string {
  if (seconds <= 0) return 'now'
  if (seconds < 60) return `${seconds}s`
  return `${Math.ceil(seconds / 60)}m`
}

const emit = defineEmits<{
  connected: [{ mapId: string; mapName: string; caltopoOpId: string | null; pollIntervalMs: number; syncData: unknown }]
  togglePause: []
  manualRefresh: []
}>()

type Step = 'loading' | 'map-list' | 'op-match' | 'error'

const authStore = useAuthStore()
const isDev = import.meta.env.DEV

const open = ref(false)
const step = ref<Step>('loading')
const maps = ref<CalTopoMap[]>([])
const errorMsg = ref('')
const selectedMapId = ref<string | null>(null)
const caltopoOps = ref<{ id: string; title: string }[]>([])
const autoMatchedOp = ref<{ id: string; title: string } | null>(null)
const selectedCaltopoOpId = ref<string | null>(null)
const initialSyncData = ref<unknown>(null)
const mapSearch = ref('')
const selectedPollIntervalMs = ref('120000')

const pollIntervalOptions = [
  { label: '30 seconds', value: '30000' },
  { label: '1 minute', value: '60000' },
  { label: '2 minutes', value: '120000' },
  { label: '5 minutes', value: '300000' },
  { label: '10 minutes', value: '600000' },
]

const filteredMaps = computed(() => {
  const q = mapSearch.value.trim().toLowerCase()
  if (!q) return maps.value
  return maps.value.filter((m) => m.title.toLowerCase().includes(q))
})

const selectedMap = computed(() => maps.value.find((m) => m.id === selectedMapId.value) ?? null)

const opOptions = computed(() => [
  { label: 'All assignments (no OP filter)', value: '' },
  ...caltopoOps.value.map((o) => ({ label: o.title, value: o.id })),
])

async function _fetchToken(): Promise<string> {
  return (await authStore.currentUser?.getIdToken()) ?? ''
}

async function loadMaps() {
  step.value = 'loading'
  errorMsg.value = ''
  maps.value = []
  mapSearch.value = ''
  selectedMapId.value = null
  try {
    const token = await _fetchToken()
    const res = await fetch('/api/caltopo/maps', {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body?.error?.message ?? `Failed to load maps (${res.status})`)
    }
    const json = await res.json()
    maps.value = json.data.maps as CalTopoMap[]
    step.value = 'map-list'
  } catch (err) {
    errorMsg.value = err instanceof Error ? err.message : 'Failed to load CalTopo maps'
    step.value = 'error'
  }
}

async function onMapSelected(mapId: string) {
  selectedMapId.value = mapId
  step.value = 'loading'
  errorMsg.value = ''

  try {
    const token = await _fetchToken()
    const res = await fetch('/api/caltopo/sync', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ map_id: mapId, cursor: 0, caltopo_op_id: null }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body?.error?.message ?? `Sync failed (${res.status})`)
    }
    const json = await res.json()
    initialSyncData.value = json.data
    caltopoOps.value = (json.data.ops ?? []) as Array<{ id: string; title: string }>

    const expected = `OP${props.opNumber}`
    const match = caltopoOps.value.find((o) => o.title === expected)
    autoMatchedOp.value = match ?? null
    selectedCaltopoOpId.value = match?.id ?? null

    step.value = 'op-match'
  } catch (err) {
    errorMsg.value = err instanceof Error ? err.message : 'Failed to fetch map data'
    step.value = 'error'
  }
}

function onConfirm() {
  if (!selectedMap.value) return
  emit('connected', {
    mapId: selectedMap.value.id,
    mapName: selectedMap.value.title,
    caltopoOpId: selectedCaltopoOpId.value || null,
    pollIntervalMs: Number(selectedPollIntervalMs.value),
    syncData: initialSyncData.value,
  })
  open.value = false
}

watch(open, (val) => {
  if (val) loadMaps()
})

function formatUpdated(epochMs: number): string {
  return new Date(epochMs).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}
</script>

<template>
  <!-- Trigger area -->
  <div v-if="!connected">
    <button
      type="button"
      class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-mono border border-border bg-surface-container-high text-on-surface rounded hover:bg-surface-container-highest transition-colors"
      @click="open = true"
    >
      <img src="/assets/caltopologo.svg" alt="CalTopo" class="w-3.5 h-3.5 shrink-0">
      Connect CalTopo
    </button>
  </div>

  <div
    v-else
    :class="[
      'items-stretch rounded border border-border bg-surface-container-high overflow-hidden',
      fullWidth ? 'flex w-full' : 'inline-flex',
    ]"
  >
    <!-- Main trigger -->
    <button
      type="button"
      :class="[
        'inline-flex items-center gap-1.5 px-2 py-1 text-xs font-mono text-on-surface hover:bg-surface-container-highest transition-colors min-w-0',
        fullWidth ? 'flex-1' : '',
      ]"
      title="Change CalTopo map"
      @click="open = true"
    >
      <img src="/assets/caltopologo.svg" alt="CalTopo" class="w-5 h-5 shrink-0">
      <span class="truncate">{{ mapName }}</span>
    </button>

    <!-- Countdown ring / pause toggle -->
    <span class="w-px bg-border shrink-0" />
    <button
      type="button"
      class="inline-flex items-center px-1.5 hover:bg-surface-container-highest transition-colors"
      :title="caltopoStore.isPaused ? 'Resume sync' : 'Pause sync'"
      @click="emit('togglePause')"
    >
      <div class="shrink-0 relative flex items-center justify-center" style="width:24px;height:24px;">
        <svg width="24" height="24" viewBox="0 0 32 32" class="-rotate-90">
          <circle cx="16" cy="16" :r="RING_R" fill="none" stroke-width="2.5" class="stroke-border" />
          <circle
            v-if="!caltopoStore.isSyncing && !caltopoStore.isPaused"
            cx="16" cy="16" :r="RING_R"
            fill="none" stroke-width="2.5"
            class="stroke-orange-400 dark:stroke-orange-500 transition-all duration-1000"
            :stroke-dasharray="RING_CIRCUMFERENCE"
            :stroke-dashoffset="ringDashOffset"
            stroke-linecap="round"
          />
          <circle
            v-else-if="caltopoStore.isSyncing"
            cx="16" cy="16" :r="RING_R"
            fill="none" stroke-width="2.5"
            class="stroke-orange-400 dark:stroke-orange-500 animate-spin origin-center"
            :stroke-dasharray="RING_CIRCUMFERENCE * 0.25 + ' ' + RING_CIRCUMFERENCE * 0.75"
            stroke-linecap="round"
          />
          <circle
            v-else
            cx="16" cy="16" :r="RING_R"
            fill="none" stroke-width="2.5"
            class="stroke-outline/40"
            :stroke-dasharray="RING_CIRCUMFERENCE"
            :stroke-dashoffset="ringDashOffset"
            stroke-linecap="round"
          />
        </svg>
        <span
          v-if="caltopoStore.isSyncing"
          class="absolute material-symbols-outlined text-[10px] text-orange-500 dark:text-orange-400"
        >sync</span>
        <span
          v-else-if="caltopoStore.isPaused"
          class="absolute material-symbols-outlined text-[10px] text-outline"
        >play_arrow</span>
        <span
          v-else-if="secondsUntilSync !== null"
          class="absolute text-[7px] font-mono text-outline leading-none"
        >{{ formatCountdown(secondsUntilSync) }}</span>
      </div>
    </button>

    <!-- Sync now + leader indicator (dev only) -->
    <template v-if="isDev">
      <span class="w-px bg-border shrink-0" />
      <button
        type="button"
        class="inline-flex items-center px-1.5 text-outline hover:text-on-surface hover:bg-surface-container-highest transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        :disabled="caltopoStore.isSyncing"
        title="Sync now (dev only)"
        @click="emit('manualRefresh')"
      >
        <span class="material-symbols-outlined !text-[13px]" :class="caltopoStore.isSyncing ? 'animate-spin' : ''">refresh</span>
      </button>
      <span class="w-px bg-border shrink-0" />
      <span
        class="inline-flex items-center px-1.5"
        :title="caltopoStore.isLeader ? 'This tab is the sync leader' : 'Another tab is leading sync'"
      >
        <span
          class="material-symbols-outlined !text-[13px] transition-colors"
          :class="caltopoStore.isLeader ? 'text-amber-500' : 'text-outline/40'"
        >wifi_tethering</span>
      </span>
    </template>
  </div>

  <!-- Connection dialog -->
  <FosDialog
    v-model:open="open"
    max-width="max-w-md"
    :title="connected ? 'Change CalTopo Map' : 'Connect CalTopo Map'"
  >
    <!-- Step indicator -->
    <div
      v-if="step === 'map-list' || step === 'op-match'"
      class="flex items-center gap-3 mb-5"
    >
      <div class="flex items-center gap-1.5">
        <span
          :class="[
            'w-2 h-2 rounded-full transition-colors',
            step === 'map-list' ? 'bg-primary' : 'bg-border',
          ]"
        />
        <span
          :class="[
            'text-[10px] font-label uppercase tracking-widest transition-colors',
            step === 'map-list' ? 'text-primary' : 'text-outline',
          ]"
        >Select Map</span>
      </div>
      <span class="flex-1 h-px bg-border" />
      <div class="flex items-center gap-1.5">
        <span
          :class="[
            'w-2 h-2 rounded-full transition-colors',
            step === 'op-match' ? 'bg-primary' : 'bg-border',
          ]"
        />
        <span
          :class="[
            'text-[10px] font-label uppercase tracking-widest transition-colors',
            step === 'op-match' ? 'text-primary' : 'text-outline',
          ]"
        >Match OP</span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="step === 'loading'" class="flex items-center justify-center py-12">
      <span class="material-symbols-outlined animate-spin text-primary text-2xl">progress_activity</span>
    </div>

    <!-- Error -->
    <div v-else-if="step === 'error'" class="flex flex-col gap-3">
      <p class="text-sm text-error">{{ errorMsg }}</p>
    </div>

    <!-- Map list -->
    <div v-else-if="step === 'map-list'" class="flex flex-col gap-2">
      <FosInput v-if="maps.length > 0" v-model="mapSearch" placeholder="Search maps…" />
      <div v-if="maps.length === 0" class="text-sm text-outline italic py-8 text-center">
        No maps found in your CalTopo account.
      </div>
      <div v-else-if="filteredMaps.length === 0" class="text-sm text-outline italic py-8 text-center">
        No maps match "{{ mapSearch }}".
      </div>
      <div
        v-else
        class="flex flex-col divide-y divide-border border border-border rounded max-h-64 overflow-y-auto"
      >
        <button
          v-for="map in filteredMaps"
          :key="map.id"
          type="button"
          class="flex items-center justify-between px-3 py-2.5 text-left hover:bg-surface-container-low transition-colors"
          @click="onMapSelected(map.id)"
        >
          <span class="font-body text-sm text-on-surface">{{ map.title }}</span>
          <span class="text-xs font-mono text-outline shrink-0 ml-3">{{ formatUpdated(map.updated) }}</span>
        </button>
      </div>
    </div>

    <!-- OP match -->
    <div v-else-if="step === 'op-match'" class="flex flex-col gap-4">
      <p class="text-xs text-outline">
        Linking: <span class="font-mono text-primary">{{ selectedMap?.title }}</span>
      </p>

      <div
        v-if="autoMatchedOp"
        class="bg-surface-container-low border border-border rounded p-3 flex items-start gap-2.5"
      >
        <span class="material-symbols-outlined text-primary text-[16px] mt-0.5 shrink-0">check_circle</span>
        <div>
          <p class="text-sm text-on-surface">
            Auto-matched to <strong class="font-mono">{{ autoMatchedOp.title }}</strong>
          </p>
          <p class="text-xs text-outline mt-0.5">Assignments will be filtered to this OP.</p>
        </div>
      </div>

      <div v-else class="flex flex-col gap-2">
        <p class="text-xs text-outline">No CalTopo OP matched "OP{{ opNumber }}". Select one or import all:</p>
        <FosSelect
          :model-value="selectedCaltopoOpId ?? ''"
          :options="opOptions"
          placeholder="Choose OP..."
          @update:model-value="selectedCaltopoOpId = $event || null"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <p class="text-[10px] font-label uppercase tracking-widest text-outline">Sync interval</p>
        <FosSelect v-model="selectedPollIntervalMs" :options="pollIntervalOptions" />
      </div>
    </div>

    <!-- Footer -->
    <template #footer>
      <template v-if="step === 'error'">
        <FosButton variant="secondary" @click="loadMaps">Retry</FosButton>
        <FosButton variant="secondary" @click="open = false">Close</FosButton>
      </template>
      <template v-else-if="step === 'map-list'">
        <FosButton variant="secondary" @click="open = false">Cancel</FosButton>
      </template>
      <template v-else-if="step === 'op-match'">
        <FosButton variant="secondary" @click="step = 'map-list'">Back</FosButton>
        <FosButton variant="primary" @click="onConfirm">Connect</FosButton>
      </template>
    </template>
  </FosDialog>
</template>
